import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetUserByIdQuery } from '@@/services/user';
import { useGetMeQuery } from '@@/services/user';
import { translateResidentType } from '@@/constants/user';
import { UserCircle2, ArrowLeft, Mail, Phone, Building2, Home, User, Shield } from 'lucide-react';

const UserPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { data: me } = useGetMeQuery();
  const { data: user } = useGetUserByIdQuery(userId || '', {
    skip: !userId || !me?.partner?.id || me?.partner?.id === userId,
  });

  if (me?.partner?.id === userId) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-card shadow-card border border-neutral-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
                  <Shield className="text-warning-600" size={24} />
                </div>
                <div>
                  <h2 className="text-heading-md text-neutral-900">Accès restreint</h2>
                  <p className="text-body-sm text-neutral-600">
                    Cet utilisateur est un administrateur. Aucune information ne sera affichée.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
        <div className="max-w-4xl mx-auto">
          {/* Header avec bouton retour */}
          <div className="mb-6">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-neutral-50 text-neutral-700 font-medium rounded-button border border-neutral-200 transition-colors duration-200 shadow-sm"
            >
              <ArrowLeft size={18} />
              <span className="text-body-sm">Retour</span>
            </button>
          </div>

          {!!user && (
            <div className="bg-white rounded-card shadow-card border border-neutral-200 overflow-hidden">
              {/* Header de la carte */}
              <div className="bg-gradient-to-r from-primary-50 to-blue-50 px-6 py-4 border-b border-primary-100">
                <div className="flex items-center space-x-3">
                  <User size={20} className="text-primary-600" />
                  <h2 className="text-heading-lg text-neutral-900">
                    Profil de l'utilisateur
                  </h2>
                </div>
              </div>

              {/* Contenu de la carte */}
              <div className="p-6">
                {/* Section photo et nom */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-6 pb-6 border-b border-neutral-200">
                  <div className="flex-shrink-0">
                    {user.image?.profile ? (
                      <img
                        src={user.image.profile}
                        alt="profile"
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover border-2 border-neutral-200"
                      />
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-neutral-100 rounded-lg flex items-center justify-center">
                        <UserCircle2 size={48} className="text-neutral-400" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl font-bold text-neutral-900 mb-1">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-body text-neutral-600">
                      {translateResidentType(user.accounts?.[0]?.residentType)}
                    </p>
                    {!!user.accounts?.[0]?.isMemberOfCondominiumCouncil && (
                      <div className="mt-2 inline-flex items-center px-3 py-1 rounded-badge text-body-xs font-medium bg-primary-100 text-primary-800">
                        <Shield size={14} className="mr-1" />
                        Membre du conseil syndical
                      </div>
                    )}
                  </div>
                </div>

                {/* Informations de contact */}
                <div className="space-y-4 mb-6">
                  <h4 className="text-heading-sm text-neutral-900 mb-3">
                    Informations de contact
                  </h4>

                  <div className="bg-neutral-50 rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <Mail size={18} className="text-primary-600 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-body-xs text-neutral-500 uppercase tracking-wide font-medium">
                          Email
                        </p>
                        <p className="text-body text-neutral-900 break-all">
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  {user.phoneNumber?.internationalNumber && (
                    <div className="bg-neutral-50 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <Phone size={18} className="text-primary-600 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-body-xs text-neutral-500 uppercase tracking-wide font-medium">
                            Téléphone
                          </p>
                          <p className="text-body text-neutral-900">
                            {user.phoneNumber.internationalNumber}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Informations du logement */}
                {user.accounts?.[0] && (
                  <div className="space-y-4">
                    <h4 className="text-heading-sm text-neutral-900 mb-3">
                      Logement
                    </h4>

                    <div className="bg-neutral-50 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <Building2 size={18} className="text-primary-600 flex-shrink-0 mt-1" />
                        <div className="min-w-0 flex-1">
                          <p className="text-body-xs text-neutral-500 uppercase tracking-wide font-medium mb-2">
                            Adresse complète
                          </p>
                          <div className="flex flex-wrap items-center gap-2 text-body text-neutral-900">
                            <span>{user.accounts[0].zoneName}</span>
                            <span className="text-neutral-400">›</span>
                            <span>{user.accounts[0].propertyName}</span>
                            <span className="text-neutral-400">›</span>
                            <span>{user.accounts[0].buildingName}</span>
                            <span className="text-neutral-400">›</span>
                            <span className="inline-flex items-center">
                              <Home size={16} className="mr-1" />
                              Appartement {user.accounts[0].apartmentNumber}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPage;
